import {
  ComposedChart,
  Bar,
  Cell,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import TrendInsightsFooter from './TrendInsightsFooter';
import { PEER_COLORS, FOCAL_COLOR, BAR_MAX_SIZE, gridProps, xAxisProps, CHART_INK, CHART_TYPE } from './chartTheme';
import { isEstimatedValue, ESTIMATED_FILL_OPACITY } from './chartNotes';
import { formatFiscalYear, formatCurrencyValue } from '../utils/formatters';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

export default function RevenueTrendChart({ data, region }: Props) {
  const chartData = data.map((d, i) => {
    const revenue = d.total_revenue ?? d.revenue_from_operations;
    const prevRevenue = i > 0
      ? (data[i - 1].total_revenue ?? data[i - 1].revenue_from_operations)
      : null;
    const yoy =
      d.revenue_yoy_growth != null
        ? d.revenue_yoy_growth * 100
        : i === 0
          ? 0   // base year anchor
          : prevRevenue != null && prevRevenue > 0 && revenue != null
            ? ((revenue - prevRevenue) / prevRevenue) * 100
            : null;
    return {
      year: formatFiscalYear(d.year, region),
      revenue,
      yoy,
      estimated: isEstimatedValue(d.data_notes, 'total_revenue', 'revenue_from_operations'),
    };
  });

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold text-neutral-800">
          Revenue Trend ({region?.currency?.unit_abbr})
        </h3>
        <p className="text-xs text-neutral-500 mt-0.5">
          Historical revenue trend with CAGR analysis
        </p>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="year" {...xAxisProps} />
            <YAxis
              yAxisId="left"
              width={80}
              domain={['auto', 'auto']}
              tick={{ fontSize: CHART_TYPE.axisTick, fill: CHART_INK.label }}
              tickFormatter={(v) => formatCurrencyValue(v, region, true)}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={['auto', 'auto']}
              tick={{ fontSize: CHART_TYPE.axisTick, fill: CHART_INK.label }}
              tickFormatter={(v) => `${v?.toFixed(0)}%`}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                if (name === 'Revenue') return [formatCurrencyValue(value, region), name];
                return [`${value?.toFixed(1)}%`, 'YoY Growth'];
              }}
            />
            <Legend />
            <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill={PEER_COLORS[0]} radius={[3, 3, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
              {chartData.map((d, i) => (
                <Cell key={i} fillOpacity={d.estimated ? ESTIMATED_FILL_OPACITY : 1} />
              ))}
            </Bar>
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="yoy"
              name="YoY %"
              stroke={FOCAL_COLOR}
              dot={{ r: 3 }}
              strokeWidth={2}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <TrendInsightsFooter
          values={data.map((d) => d.total_revenue ?? d.revenue_from_operations ?? null)}
          metricLabel="Revenue"
        />
      </CardBody>
    </Card>
  );
}
